import { get, has, keyBy } from 'lodash';
import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, { Component, Fragment } from 'react';
import { FormattedMessage } from 'react-intl';
import { Form, Tooltip } from 'reactstrap';

import globalTranslations from '../../../i18n/globalTranslations';
import CustomFormField from '../../../models/CustomFormField';
import { createForm } from '../../../models/Form';
import { createFormField } from '../../../models/FormField';
import { modelOf } from '../../../prop-types';
import FormValidator from '../../../services/FormValidator';
import AccountStore from '../../../store/AccountStore';
import ConfigStore from '../../../store/ConfigStore';
import CountryStore from '../../../store/CountryStore';
import LanguageStore from '../../../store/LanguageStore';
import FormFieldId from '../../../types/FormFieldId';
import FormFieldType from '../../../types/FormFieldType';
import { fieldTypeLabel, labels } from '../../../util/formLabels';
import {
  finnishBusinessId,
  maxLength,
  notBlank,
  privacyStatementAccepted,
} from '../../../util/formValidators';
import Icon from '../../common/Icon';
import PrivacyStatementLink from '../../legal/PrivacyStatementLink';
import CheckboxField from '../CheckboxField';
import FormGroupField from '../FormGroupField';
import RadioField from '../RadioField';
import SelectField from '../SelectField';

const FORM_MESSAGE_ROWS = 8;

function createOption(value, label) {
  return {
    value,
    label,
  };
}

@observer
export class CustomForm extends Component {
  form = null;

  state = {
    businessIdHelpVisible: false,
  };

  constructor(props) {
    super(props);

    this.initializeForm();
  }

  initializeForm = () => {
    this.form = createForm(this.createCustomForm());
  };

  createCustomForm = () => {
    const { fields } = this.props;
    const formFields = {};

    fields.forEach((field, index) => {
      const fieldContent = this.createCustomField(field);
      formFields[fieldContent.key] = createFormField(
        {
          externalIndex: index,
          value: fieldContent.value,
        },
        fieldContent.validator,
        fieldContent.isActive
      );
    });
    return formFields;
  };

  createCustomField = (field) => {
    const { accountStore, countryStore, formValidator } = this.props;
    const defaultValue = {};
    let valueValidator = [];
    const customField = {
      key: null,
      value: null,
      isActive: null,
      label: null,
      maxLength: null,
    };

    const validateBusinessId =
      countryStore.countries.length === 1 &&
      has(keyBy(countryStore.countries, 'iso_code_2'), 'FI');

    if (field.type === FormFieldType.BOOLEAN) {
      defaultValue.value = field.value ? '1' : '0';
    } else {
      if (field.value) {
        defaultValue.value = field.value;
      }
    }

    switch (field.id) {
      case FormFieldId.BILLING_IS_COMPANY:
        customField.key = fieldTypeLabel.BILLING_IS_COMPANY;
        break;
      case FormFieldId.DELIVERY_IS_COMPANY:
        customField.key = fieldTypeLabel.DELIVERY_IS_COMPANY;
        customField.isActive = () =>
          this.form.fields.get(fieldTypeLabel.SEPARATE_DELIVERY_ADDRESS)
            .value === '1' && !accountStore.loggedIn;
        break;
      case FormFieldId.BILLING_CITY:
        customField.key = fieldTypeLabel.CITY;
        valueValidator = formValidator.city(labels.city);
        customField.label = labels.city;
        if (field.required) {
          valueValidator.push(notBlank(labels.city));
        }
        break;
      case FormFieldId.DELIVERY_CITY:
        customField.key = fieldTypeLabel.DELIVERY_CITY;
        customField.isActive = () =>
          this.form.fields.get(fieldTypeLabel.SEPARATE_DELIVERY_ADDRESS)
            .value === '1' && !accountStore.loggedIn;
        customField.label = labels.city;
        valueValidator = formValidator.city(labels.city);
        if (field.required) {
          valueValidator.push(notBlank(labels.city));
        }
        break;
      case FormFieldId.BILLING_COMPANY:
        customField.key = fieldTypeLabel.COMPANY;
        if (field.required) {
          valueValidator.push(notBlank(labels.company));
        }

        if (!accountStore.isRetailer) {
          customField.isActive = () =>
            this.form.fields.get(fieldTypeLabel.BILLING_IS_COMPANY).value ===
            '1';
        }
        customField.label = labels.company;
        break;
      case FormFieldId.DELIVERY_COMPANY:
        customField.key = fieldTypeLabel.DELIVERY_COMPANY;
        customField.isActive = () =>
          this.form.fields.get(fieldTypeLabel.SEPARATE_DELIVERY_ADDRESS)
            .value === '1' &&
          this.form.fields.get(fieldTypeLabel.DELIVERY_IS_COMPANY).value ===
            '1' &&
          !accountStore.loggedIn;
        if (field.required) {
          valueValidator.push(notBlank(labels.company));
        }
        customField.label = labels.company;
        break;
      case FormFieldId.BILLING_COUNTRY:
        customField.key = fieldTypeLabel.COUNTRY;

        if (!defaultValue.value) {
          defaultValue.value = this.getCountryId();
        }
        if (field.required) {
          valueValidator.push(notBlank(labels.country));
        }
        customField.label = labels.country;
        break;
      case FormFieldId.DELIVERY_COUNTRY:
        customField.key = fieldTypeLabel.DELIVERY_COUNTRY;
        customField.isActive = () =>
          this.form.fields.get(fieldTypeLabel.SEPARATE_DELIVERY_ADDRESS)
            .value === '1' && !accountStore.loggedIn;

        if (!defaultValue.value) {
          defaultValue.value = this.getCountryId();
        }
        if (field.required) {
          valueValidator.push(notBlank(labels.country));
        }
        customField.label = labels.country;
        break;
      case FormFieldId.BILLING_EMAIL:
        customField.key = 'email';
        valueValidator = formValidator.email(labels.email);
        if (field.required) {
          valueValidator.push(notBlank(labels.email));
        }
        break;
      case FormFieldId.BILLING_NAME:
        customField.key = fieldTypeLabel.NAME;
        valueValidator = formValidator.fullName(labels.name);
        if (field.required) {
          valueValidator.push(notBlank(labels.name));
        }
        customField.label = labels.name;
        break;
      case FormFieldId.DELIVERY_NAME:
        customField.key = fieldTypeLabel.DELIVERY_NAME;
        customField.isActive = () =>
          this.form.fields.get(fieldTypeLabel.SEPARATE_DELIVERY_ADDRESS)
            .value === '1' && !accountStore.loggedIn;
        valueValidator = formValidator.fullName(labels.name);
        if (field.required) {
          valueValidator.push(notBlank(labels.name));
        }
        customField.label = labels.name;
        break;
      case FormFieldId.BILLING_PHONE:
        customField.key = fieldTypeLabel.PHONE;
        valueValidator = formValidator.telephone(labels.telephone);
        if (field.required) {
          valueValidator.push(notBlank(labels.telephone));
        }
        customField.label = labels.telephone;
        break;
      case FormFieldId.DELIVERY_PHONE:
        customField.key = fieldTypeLabel.DELIVERY_PHONE;
        customField.isActive = () =>
          this.form.fields.get(fieldTypeLabel.SEPARATE_DELIVERY_ADDRESS)
            .value === '1' && !accountStore.loggedIn;
        valueValidator = formValidator.telephone(labels.telephone);
        if (field.required) {
          valueValidator.push(notBlank(labels.telephone));
        }
        customField.label = labels.telephone;
        break;
      case FormFieldId.BILLING_POSTAL_CODE:
        customField.key = fieldTypeLabel.POSTAL_CODE;
        valueValidator = formValidator.postalCode(labels.postalCode);
        if (field.required) {
          valueValidator.push(notBlank(labels.postalCode));
        }
        customField.label = labels.postalCode;
        break;
      case FormFieldId.DELIVERY_POSTAL_CODE:
        customField.key = fieldTypeLabel.DELIVERY_POSTAL_CODE;
        customField.isActive = () =>
          this.form.fields.get(fieldTypeLabel.SEPARATE_DELIVERY_ADDRESS)
            .value === '1' && !accountStore.loggedIn;
        valueValidator = formValidator.postalCode(labels.postalCode);
        if (field.required) {
          valueValidator.push(notBlank(labels.postalCode));
        }
        customField.label = labels.postalCode;
        break;
      case FormFieldId.BILLING_STREET_ADDRESS:
        customField.key = fieldTypeLabel.STREET_ADDRESS;
        valueValidator = formValidator.streetAddress(labels.streetAddress);
        if (field.required) {
          valueValidator.push(notBlank(labels.streetAddress));
        }
        customField.label = labels.streetAddress;
        break;
      case FormFieldId.DELIVERY_STREET_ADDRESS:
        customField.key = fieldTypeLabel.DELIVERY_STREET_ADDRESS;
        customField.isActive = () =>
          this.form.fields.get(fieldTypeLabel.SEPARATE_DELIVERY_ADDRESS)
            .value === '1' && !accountStore.loggedIn;
        valueValidator = formValidator.streetAddress(labels.streetAddress);
        if (field.required) {
          valueValidator.push(notBlank(labels.streetAddress));
        }
        customField.label = labels.streetAddress;
        break;
      case FormFieldId.BILLING_VAT_NUMBER:
        customField.key = fieldTypeLabel.VAT_NUMBER;
        if (field.required) {
          valueValidator.push(
            validateBusinessId
              ? finnishBusinessId()
              : notBlank(labels.businessId)
          );
        }
        if (!accountStore.isRetailer) {
          customField.isActive = () =>
            this.form.fields.get(fieldTypeLabel.BILLING_IS_COMPANY).value ===
            '1';
        }
        customField.label = labels.businessId;
        break;
      case FormFieldId.DELIVERY_ADDRESS_DROPDOWN:
        customField.key = fieldTypeLabel.DELIVERY_ADDRESS_DROPDOWN;
        customField.isActive = () =>
          this.form.fields.get(fieldTypeLabel.SEPARATE_DELIVERY_ADDRESS)
            .value === '1' && accountStore.loggedIn;
        break;
      case FormFieldId.EXTRA_MESSAGE:
        customField.key = fieldTypeLabel.EXTRA_MESSAGE;
        if (field.required) {
          valueValidator.push(notBlank());
        }
        break;
      case FormFieldId.FIRSTNAME:
        customField.key = fieldTypeLabel.FIRSTNAME;
        valueValidator = formValidator.firstName(labels.firstName);
        if (field.required) {
          valueValidator.push(notBlank(labels.firstName));
        }
        customField.label = labels.firstName;
        break;
      case FormFieldId.LASTNAME:
        customField.key = fieldTypeLabel.LASTNAME;
        valueValidator = formValidator.lastName(labels.lastName);
        if (field.required) {
          valueValidator.push(notBlank(labels.lastName));
        }
        customField.label = labels.lastName;
        break;
      case FormFieldId.PRIVACY_STATEMENT:
        defaultValue.value = field.value ? '1' : '';
        customField.key = fieldTypeLabel.PRIVACY_STATEMENT;
        if (field.required) {
          valueValidator.push(
            privacyStatementAccepted(labels.privacyStatement)
          );
        }
        break;
      case FormFieldId.SEPARATE_DELIVERY_ADDRESS:
        customField.key = fieldTypeLabel.SEPARATE_DELIVERY_ADDRESS;
        customField.value = field.value;
        break;
      default:
        break;
    }

    if (Number(field.max_length) > 0) {
      valueValidator.push(maxLength(customField.label, field.max_length));
    }

    customField.value = defaultValue.value;
    if (valueValidator) {
      customField.validator = valueValidator;
    }
    return customField;
  };

  handleOnSubmit = (event) => {
    const { onSubmit } = this.props;

    event.preventDefault();
    this.form.validate();
    if (this.form.valid && onSubmit) {
      onSubmit(this.form.getFieldValues());
    }
  };

  handleChange = (event) => {
    const { onChange } = this.props;
    const field = this.form.fields.get(event.target.name);

    field.setValue(event.target.value);
    event.preventDefault();
    this.form.validate();

    if (onChange) {
      onChange(field, this.form.valid);
    }
  };

  passCheckboxValue = (field) => {
    const { onChange } = this.props;

    this.form.validate();

    if (onChange) {
      onChange(field, this.form.valid);
    }
  };

  getCountryId = () => {
    const { accountStore, countryStore } = this.props;
    const defaultId = accountStore.account && accountStore.account.country_id;

    return defaultId || get(countryStore, 'countries[0].id');
  };

  toggleBusinessIdHelp = (e) => {
    e.preventDefault();
    this.setState({
      businessIdHelpVisible: !this.state.businessIdHelpVisible,
    });
  };

  businessIdTooltip() {
    const { languageStore, countryStore } = this.props;

    const validateBusinessId =
      countryStore.countries.length === 1 &&
      has(keyBy(countryStore.countries, 'iso_code_2'), 'FI');

    if (!validateBusinessId) {
      return null;
    }

    const ytjLanguageIdDict = {
      fi: 1,
      sv: 2,
      en: 3,
    };
    const languageId =
      ytjLanguageIdDict[languageStore.activeLocale] || ytjLanguageIdDict.en;
    const url = `https://tietopalvelu.ytj.fi/yrityshaku.aspx?kielikoodi=${languageId}`;
    return (
      <span>
        <Tooltip
          placement="right"
          isOpen={this.state.businessIdHelpVisible}
          target="BusinessIdTooltip"
          toggle={this.toggle}
        >
          <FormattedMessage
            {...globalTranslations.businessIdHelp}
            values={{
              link: (
                <a href={url} target="_blank" rel="noopener">
                  <FormattedMessage {...globalTranslations.hereSentence} />
                </a>
              ),
            }}
          />
        </Tooltip>
        <span id="BusinessIdTooltip" onClick={this.toggleBusinessIdHelp}>
          <Icon name="question-circle" />
        </span>
      </span>
    );
  }

  field = (fieldName, props) => {
    const field = this.form.fields.get(fieldName);

    if (!field) {
      return null;
    }

    return (
      field.active && (
        <FormGroupField
          key={fieldName}
          field={field}
          formName={this.props.formName}
          fieldName={fieldName}
          label={labels[fieldName]}
          {...props}
        />
      )
    );
  };

  checkbox = (fieldName, props) => {
    const field = this.form.fields.get(fieldName);

    if (!field) {
      return null;
    }

    return (
      field.active && (
        <CheckboxField
          key={fieldName}
          field={field}
          formName={this.props.formName}
          fieldName={fieldName}
          {...props}
        />
      )
    );
  };

  radio = (fieldName, props) => {
    const field = this.form.fields.get(fieldName);

    if (!field) {
      return null;
    }

    return (
      field.active && (
        <RadioField
          key={fieldName}
          field={field}
          formName={this.props.formName}
          fieldName={fieldName}
          label={labels[fieldName]}
          {...props}
        />
      )
    );
  };

  dropdown = (fieldName, props) => {
    const field = this.form.fields.get(fieldName);

    return (
      field.active && (
        <SelectField
          key={fieldName}
          field={field}
          formName={this.props.formName}
          fieldName={fieldName}
          label={labels[fieldName]}
          handleChange={this.handleDropdownChange}
          {...props}
        />
      )
    );
  };

  renderFields = () => {
    const { fields } = this.props;

    return fields.map((field, index) => {
      let toRender = null;
      const fieldProps = {
        required: field.required,
        handleChange: this.handleChange,
      };

      switch (field.type) {
        case FormFieldType.TEXTAREA:
          fieldProps.type = 'textarea';
          break;
        case FormFieldType.BOOLEAN:
          fieldProps.type = 'radio';
          break;
        case FormFieldType.SELECT:
          fieldProps.type = 'select';
          break;
        case FormFieldType.TEXT:
        case FormFieldType.PHONE:
        case FormFieldType.EMAIL:
        default:
          fieldProps.type = 'text';
          break;
      }

      switch (field.id) {
        case FormFieldId.BILLING_IS_COMPANY:
          toRender = this.radio(fieldTypeLabel.BILLING_IS_COMPANY, {
            options: [
              createOption('0', labels.no),
              createOption('1', labels.yes),
            ],
            ...fieldProps,
          });
          break;
        case FormFieldId.DELIVERY_IS_COMPANY:
          const visible =
            this.form.fields.get(fieldTypeLabel.SEPARATE_DELIVERY_ADDRESS)
              .value === '1';
          toRender = (
            <Fragment>
              <div
                className="CustomForm__header"
                style={{
                  height: visible ? 'auto' : '0',
                  visibility: visible ? 'visible' : 'hidden',
                }}
              >
                <FormattedMessage
                  {...globalTranslations.recipientInformationTitle}
                />
              </div>
              {this.radio(fieldTypeLabel.DELIVERY_IS_COMPANY, {
                options: [
                  createOption('0', labels.no),
                  createOption('1', labels.yes),
                ],
                ...fieldProps,
              })}
            </Fragment>
          );
          break;
        case FormFieldId.BILLING_CITY:
          toRender = this.field(fieldTypeLabel.CITY, {
            autoComplete: 'address-level2',
            ...fieldProps,
          });
          break;
        case FormFieldId.DELIVERY_CITY:
          toRender = this.field(fieldTypeLabel.DELIVERY_CITY, {
            autoComplete: 'address-level2',
            ...fieldProps,
          });
          break;
        case FormFieldId.BILLING_COMPANY:
          toRender = this.field(fieldTypeLabel.COMPANY, {
            autoComplete: 'organization',
            ...fieldProps,
          });
          break;
        case FormFieldId.DELIVERY_COMPANY:
          toRender = this.field(fieldTypeLabel.DELIVERY_COMPANY, {
            autoComplete: 'organization',
            ...fieldProps,
          });
          break;
        case FormFieldId.BILLING_COUNTRY:
          toRender = this.field(fieldTypeLabel.COUNTRY, {
            ...fieldProps,
          });
          break;
        case FormFieldId.DELIVERY_COUNTRY:
          toRender = this.field(fieldTypeLabel.DELIVERY_COUNTRY, {
            ...fieldProps,
          });
          break;
        case FormFieldId.BILLING_EMAIL:
          toRender = this.field(fieldTypeLabel.EMAIL, {
            autoComplete: 'email',
            autoCorrect: 'off',
            autoCapitalize: 'none',
            ...fieldProps,
          });
          break;
        case FormFieldId.BILLING_NAME:
          toRender = this.field(fieldTypeLabel.NAME, {
            label: labels.name,
            ...fieldProps,
          });
          break;
        case FormFieldId.DELIVERY_NAME:
          toRender = this.field(fieldTypeLabel.DELIVERY_NAME, {
            label: labels.name,
            ...fieldProps,
          });
          break;
        case FormFieldId.BILLING_PHONE:
          toRender = this.field(fieldTypeLabel.PHONE, {
            autoComplete: 'tel-national',
            ...fieldProps,
          });
          break;
        case FormFieldId.DELIVERY_PHONE:
          toRender = this.field(fieldTypeLabel.DELIVERY_PHONE, {
            autoComplete: 'tel-national',
            ...fieldProps,
          });
          break;
        case FormFieldId.BILLING_POSTAL_CODE:
          toRender = this.field(fieldTypeLabel.POSTAL_CODE, {
            autoComplete: 'postal-code',
            ...fieldProps,
          });
          break;
        case FormFieldId.DELIVERY_POSTAL_CODE:
          toRender = this.field(fieldTypeLabel.DELIVERY_POSTAL_CODE, {
            autoComplete: 'postal-code',
            ...fieldProps,
          });
          break;
        case FormFieldId.BILLING_STREET_ADDRESS:
          toRender = this.field(fieldTypeLabel.STREET_ADDRESS, {
            autoComplete: 'street-address',
            ...fieldProps,
          });
          break;
        case FormFieldId.DELIVERY_STREET_ADDRESS:
          toRender = this.field(fieldTypeLabel.DELIVERY_STREET_ADDRESS, {
            autoComplete: 'street-address',
            ...fieldProps,
          });
          break;
        case FormFieldId.BILLING_VAT_NUMBER:
          toRender = this.field(fieldTypeLabel.VAT_NUMBER, {
            label: (
              <span>
                {labels.businessId} {this.businessIdTooltip()}
              </span>
            ),
            // We don't want autocomplete here, so we put in a non-existent string
            autoComplete: 'businessId',
            ...fieldProps,
          });
          break;
        case FormFieldId.DELIVERY_ADDRESS_DROPDOWN:
          toRender = this.dropdown(fieldTypeLabel.DELIVERY_ADDRESS_DROPDOWN, {
            options: field.options.map((option) =>
              createOption(String(option.value), option.text)
            ),
            ...fieldProps,
          });
          break;
        case FormFieldId.EXTRA_MESSAGE:
          toRender = this.field(fieldTypeLabel.EXTRA_MESSAGE, {
            label: <span>{field.name}</span>,
            rows: FORM_MESSAGE_ROWS,
            ...fieldProps,
          });
          break;
        case FormFieldId.FIRSTNAME:
          toRender = this.field(fieldTypeLabel.FIRSTNAME, {
            label: labels.firstName,
            autoComplete: 'given-name',
            ...fieldProps,
          });
          break;
        case FormFieldId.LASTNAME:
          toRender = this.field(fieldTypeLabel.LASTNAME, {
            label: labels.lastName,
            autoComplete: 'family-name',
            ...fieldProps,
          });
          break;
        case FormFieldId.PRIVACY_STATEMENT:
          // TODO pitää tulla bäkkäriltä
          fieldProps.type = 'checkbox';
          // Use CheckboxField own handling for value.
          delete fieldProps.handleChange;
          fieldProps.updateExternalValue = this.passCheckboxValue;

          toRender = (
            <Fragment>
              {this.checkbox(fieldTypeLabel.PRIVACY_STATEMENT, {
                options: createOption('1', labels.privacyStatement),
                ...fieldProps,
              })}
              <div className="CustomForm__privacy-statement-link-wrapper">
                (<PrivacyStatementLink />)
              </div>
            </Fragment>
          );
          break;
        case FormFieldId.SEPARATE_DELIVERY_ADDRESS:
          toRender = this.radio(fieldTypeLabel.SEPARATE_DELIVERY_ADDRESS, {
            options: [
              createOption('0', labels.no),
              createOption('1', labels.yes),
            ],
            ...fieldProps,
          });
          break;
        default:
          break;
      }

      return <Fragment key={index}>{toRender}</Fragment> || null;
    });
  };

  render() {
    const { className } = this.props;

    return (
      <Form className={className} noValidate onSubmit={this.handleOnSubmit}>
        {this.renderFields()}
      </Form>
    );
  }
}

CustomForm.propTypes = {
  accountStore: modelOf(AccountStore).isRequired,
  configStore: modelOf(ConfigStore).isRequired,
  countryStore: modelOf(CountryStore).isRequired,
  languageStore: modelOf(LanguageStore).isRequired,
  fields: PropTypes.arrayOf(modelOf(CustomFormField)).isRequired,
  formValidator: PropTypes.instanceOf(FormValidator).isRequired,
  formName: PropTypes.string.isRequired,
  onSubmit: PropTypes.func,
  onChange: PropTypes.func,
  className: PropTypes.string,
};

export default inject(
  'accountStore',
  'configStore',
  'countryStore',
  'languageStore',
  'formValidator'
)(CustomForm);
